/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {AggregatorSignalKey, POWER_TYPES, PowerType} from '../../model/aggregator-signal.models';

/** ICDEnums.ICDPlatform dbVals. Create-modal architecture list; default SDV-2. */
export const ICD_PLATFORMS = ['GLOBAL-A', 'GLOBAL-B', 'SDV-2'] as const;
export const DEFAULT_ICD_PLATFORM = 'SDV-2';

export type KeywordVisibility = 'hidden' | 'readonly' | 'dropdown';

export function normalizeKeyword(keyword: string | null | undefined): string {
    return (keyword ?? '').trim().toUpperCase();
}

export function normalizeArchitecture(architecture: string | null | undefined): string {
    return (architecture ?? '').trim();
}

export function distinctObjectCodes(keys: AggregatorSignalKey[]): string[] {
    const codes = new Set<string>();
    for (const key of keys || []) {
        const code = (key.objectCode || '').trim();
        if (code) {
            codes.add(code);
        }
    }
    return [...codes].sort((a, b) => a.localeCompare(b));
}

export function architecturesForObjectCode(keys: AggregatorSignalKey[], objectCode: string): string[] {
    const normalizedCode = (objectCode || '').trim();
    if (!normalizedCode) {
        return [];
    }
    const architectures = new Set<string>();
    for (const key of keys || []) {
        if ((key.objectCode || '').trim() !== normalizedCode) {
            continue;
        }
        const architecture = normalizeArchitecture(key.architecture);
        if (architecture) {
            architectures.add(architecture);
        }
    }
    return [...architectures].sort((a, b) => a.localeCompare(b));
}

export function powerTypesForObjectCodeArchitecture(keys: AggregatorSignalKey[], objectCode: string,
                                                    architecture: string): PowerType[] {
    const normalizedCode = (objectCode || '').trim();
    const normalizedArchitecture = normalizeArchitecture(architecture);
    if (!normalizedCode || !normalizedArchitecture) {
        return [];
    }
    const types = new Set<PowerType>();
    for (const key of keys || []) {
        if ((key.objectCode || '').trim() === normalizedCode
            && normalizeArchitecture(key.architecture) === normalizedArchitecture
            && key.powerType) {
            types.add(key.powerType as PowerType);
        }
    }
    return POWER_TYPES.filter(type => types.has(type));
}

export function keywordsForScope(keys: AggregatorSignalKey[], objectCode: string, architecture: string,
                                 powerType: string): string[] {
    const normalizedCode = (objectCode || '').trim();
    const normalizedArchitecture = normalizeArchitecture(architecture);
    const normalizedPowerType = (powerType || '').trim();
    if (!normalizedCode || !normalizedArchitecture || !normalizedPowerType) {
        return [];
    }
    const keywords = new Set<string>();
    for (const key of keys || []) {
        if ((key.objectCode || '').trim() === normalizedCode
            && normalizeArchitecture(key.architecture) === normalizedArchitecture
            && (key.powerType || '').trim() === normalizedPowerType) {
            keywords.add(normalizeKeyword(key.keyword));
        }
    }
    return [...keywords];
}

export function keywordVisibility(keywords: string[]): KeywordVisibility {
    const unique = [...new Set((keywords || []).map(normalizeKeyword))];
    if (unique.length === 0 || (unique.length === 1 && unique[0] === '')) {
        return 'hidden';
    }
    if (unique.length === 1) {
        return 'readonly';
    }
    return 'dropdown';
}

/** Empty unused option first when present, then named variants A–Z. Never labeled Original. */
export function keywordDropdownOptions(keywords: string[]): string[] {
    const unique = [...new Set((keywords || []).map(normalizeKeyword))];
    const named = unique.filter(keyword => keyword !== '').sort((a, b) => a.localeCompare(b));
    return unique.includes('') ? ['', ...named] : named;
}

/** Clear is only valid when unused (blank) keyword is one of the real options. */
export function keywordShowClear(selected: string | null | undefined, keywords: string[]): boolean {
    return !!normalizeKeyword(selected) && keywordDropdownOptions(keywords).includes('');
}

export function keyExists(keys: AggregatorSignalKey[], objectCode: string, architecture: string,
                          powerType: string, keyword?: string | null): boolean {
    const normalizedCode = (objectCode || '').trim();
    const normalizedArchitecture = normalizeArchitecture(architecture);
    const normalizedPowerType = (powerType || '').trim();
    const normalizedKeyword = normalizeKeyword(keyword);
    if (!normalizedCode || !normalizedArchitecture || !normalizedPowerType) {
        return false;
    }
    return (keys || []).some(key =>
        (key.objectCode || '').trim() === normalizedCode
        && normalizeArchitecture(key.architecture) === normalizedArchitecture
        && (key.powerType || '').trim() === normalizedPowerType
        && normalizeKeyword(key.keyword) === normalizedKeyword
    );
}

export function keyLabel(key: AggregatorSignalKey): string {
    const keyword = normalizeKeyword(key?.keyword);
    const base = `${key?.objectCode || ''}-${normalizeArchitecture(key?.architecture)}-${key?.powerType || ''}`;
    return keyword ? `${base}-${keyword}` : base;
}
